import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/Button'
import { Checkbox, Field, Input, Textarea } from '@/components/ui/Field'
import { Page, PageHeader, Section } from '@/components/ui/Page'
import { EmptyState, ErrorState, Loading } from '@/components/ui/States'
import { formatDateShort } from '@/lib/dates'
import { formatPhone, pluralise } from '@/lib/format'
import { JOB_KIND_LABEL } from '@/lib/jobs'
import { describeError } from '@/lib/supabase'
import { useContacts, useSaveContact, type ContactWithJobs } from './queries'

const fullName = (contact: { first_name: string; last_name: string | null }) =>
  [contact.first_name, contact.last_name].filter(Boolean).join(' ')

/**
 * Contacts (28 Oct 2026): everyone in the system in one list, to find,
 * correct and add. Each person shows the projects they are on; a change here
 * shows on those projects straight away, because they read the same row.
 */
export function ContactsPage() {
  const [search, setSearch] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [editing, setEditing] = useState<ContactWithJobs | 'new' | null>(null)
  const query = useContacts(showArchived)

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase()
    const all = query.data ?? []
    if (!needle) return all
    return all.filter((contact) =>
      [fullName(contact), contact.email, contact.phone, ...contact.jobs.map((job) => job.title)]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle)),
    )
  }, [query.data, search])

  return (
    <Page>
      <PageHeader
        eyebrow="Contacts"
        title="Contacts"
        meta={query.data ? <span>{pluralise(query.data.length, 'person', 'people')}</span> : null}
        actions={
          <Button variant="primary" onClick={() => setEditing('new')}>
            Add a contact
          </Button>
        }
      />

      {editing ? (
        <ContactForm
          key={editing === 'new' ? 'new' : editing.id}
          contact={editing === 'new' ? null : editing}
          onDone={() => setEditing(null)}
        />
      ) : null}

      <div className="mb-3 flex flex-wrap items-end gap-4">
        <Field label="Search" className="w-full sm:w-72">
          <Input type="search" value={search} placeholder="Name, email or phone" onChange={(event) => setSearch(event.target.value)} />
        </Field>
        <Checkbox label="Show archived" checked={showArchived} onChange={(event) => setShowArchived(event.target.checked)} className="pb-2" />
      </div>

      {query.isPending ? (
        <Loading label="Loading contacts" />
      ) : query.isError ? (
        <ErrorState title="Contacts could not load" detail={describeError(query.error)} onRetry={() => void query.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={query.data.length ? 'Nobody matches that' : 'No contacts yet'}
          body="Couples and clients are added here when you make a project, or by hand."
          action={{ label: 'Add a contact', onClick: () => setEditing('new') }}
        />
      ) : (
        <div className="card overflow-x-auto px-2">
          <table className="ledger min-w-[44rem]">
            <thead>
              <tr>
                <th className="label">Name</th>
                <th className="label">Email</th>
                <th className="label">Phone</th>
                <th className="label">Projects</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((contact) => (
                <tr key={contact.id} className={contact.archived_at ? 'opacity-60' : undefined}>
                  <td>
                    <button type="button" className="text-left font-semibold hover:underline" onClick={() => setEditing(contact)}>
                      {fullName(contact)}
                    </button>
                  </td>
                  <td className="text-taupe">
                    {contact.email ? (
                      <a href={`mailto:${contact.email}`} className="hover:underline">
                        {contact.email}
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="whitespace-nowrap text-taupe">
                    {contact.phone ? (
                      <a href={`tel:${contact.phone}`} className="hover:underline">
                        {formatPhone(contact.phone)}
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="text-taupe">
                    {contact.jobs.length
                      ? contact.jobs.map((job, index) => (
                          <span key={job.id}>
                            {index ? ', ' : ''}
                            <Link to={`/weddings/${job.id}`} className="hover:underline">
                              {job.title && job.kind !== 'wedding' ? job.title : JOB_KIND_LABEL[job.kind]}, {formatDateShort(job.event_date)}
                            </Link>
                          </span>
                        ))
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Page>
  )
}

function ContactForm({ contact, onDone }: { contact: ContactWithJobs | null; onDone: () => void }) {
  const save = useSaveContact()
  const [form, setForm] = useState({
    first_name: contact?.first_name ?? '',
    last_name: contact?.last_name ?? '',
    email: contact?.email ?? '',
    phone: contact?.phone ?? '',
    notes: contact?.notes ?? '',
  })
  const set = (key: keyof typeof form) => (event: { target: { value: string } }) => setForm({ ...form, [key]: event.target.value })
  const orNull = (value: string) => value.trim() || null

  function submit(event: React.FormEvent) {
    event.preventDefault()
    if (!form.first_name.trim()) {
      toast.error('Not saved yet', { description: 'A first name is needed.' })
      return
    }
    save.mutate(
      {
        id: contact?.id ?? null,
        values: {
          first_name: form.first_name.trim(),
          last_name: orNull(form.last_name),
          email: orNull(form.email),
          phone: orNull(form.phone),
          notes: orNull(form.notes),
        },
      },
      {
        onSuccess: () => {
          toast.success(contact ? 'Contact saved' : 'Contact added')
          onDone()
        },
        onError: (error) => toast.error('Not saved', { description: describeError(error) }),
      },
    )
  }

  function toggleArchive() {
    if (!contact) return
    save.mutate(
      { id: contact.id, values: { first_name: contact.first_name, archived_at: contact.archived_at ? null : new Date().toISOString() } },
      { onSuccess: onDone, onError: (error) => toast.error('Not saved', { description: describeError(error) }) },
    )
  }

  return (
    <Section title={contact ? fullName(contact) : 'Add a contact'}>
      <form noValidate onSubmit={submit} className="flex flex-col gap-4">
        <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="First name" required>
            <Input value={form.first_name} onChange={set('first_name')} />
          </Field>
          <Field label="Surname">
            <Input value={form.last_name} onChange={set('last_name')} />
          </Field>
          <Field label="Email">
            <Input type="email" value={form.email} onChange={set('email')} />
          </Field>
          <Field label="Phone">
            <Input type="tel" value={form.phone} onChange={set('phone')} />
          </Field>
        </div>
        <Field label="Notes">
          <Textarea rows={3} value={form.notes} onChange={set('notes')} />
        </Field>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" variant="primary" loading={save.isPending} loadingLabel="Saving">
            Save
          </Button>
          <Button variant="ghost" onClick={onDone}>
            Cancel
          </Button>
          {contact ? (
            <Button variant="ghost" className="ml-auto" onClick={toggleArchive}>
              {contact.archived_at ? 'Restore' : 'Archive'}
            </Button>
          ) : null}
        </div>
      </form>
    </Section>
  )
}
